import { FC, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Language } from '../../../common/CountryMarketConfig';
import { Button } from '../../../components/Button';
import { Heading } from '../../../components/Heading';
import { useCountryMarketState } from '../../../controllers/CountryMarketController/CountryMarketState';
import { getAvailableTaxonomiesFromState } from '../../../controllers/TaxonomyController/TaxonomyHelper';
import { useTaxonomyState } from '../../../controllers/TaxonomyController/TaxonomyState';
import { noOp, stripHTMLTags } from '../../../lib/utils';
import { Recommendation } from '../RecommendationsSection';

type RecommendationTileProps = {
  recommendation: Recommendation;
  placeholderImage: string;
  onDisplay?: (recommendation: Recommendation) => void;
  onClick?: (recommendation: Recommendation) => void;
};

export const RecommendationTile: FC<RecommendationTileProps> = ({
  recommendation,
  placeholderImage,
  onDisplay = noOp,
  onClick = noOp,
}: RecommendationTileProps) => {
  const { t, i18n } = useTranslation();

  const [{ countryMarketConfig }] = useCountryMarketState();
  const [taxonomyState] = useTaxonomyState();

  const availableTaxonomies = useMemo(
    () =>
      getAvailableTaxonomiesFromState(
        taxonomyState,
        countryMarketConfig,
        i18n.language as Language,
      ),
    [countryMarketConfig, i18n.language, taxonomyState],
  );

  const languageKey = i18n.language;
  const defaultLanguageKey = 'en_US';

  const getNameforTaxonomyId = useCallback(
    (taxonomyId: string): string | boolean => {
      const matchingTaxonomy = availableTaxonomies[taxonomyId];
      if (!matchingTaxonomy) return false;
      const matchingTaxonomyName = availableTaxonomies[taxonomyId].find(
        ({ locale }) =>
          locale === languageKey.toLowerCase() ||
          locale === defaultLanguageKey.toLowerCase(),
      );
      return matchingTaxonomyName?.data || false;
    },
    [availableTaxonomies, languageKey],
  );

  const ImageStyle = useMemo(
    () => ({
      backgroundImage: `url(${recommendation.image_url || placeholderImage})`,
    }),
    [placeholderImage, recommendation.image_url],
  );

  const MarketingClaimStyle = {
    padding: 5,
    fontSize: 12,
    letterSpacing: 3,
  };

  useEffect(() => {
    onDisplay(recommendation);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recommendation]);

  return (
    <div className="h-full pb-6">
      <div className="flex flex-col h-full rounded border border-gray-300 text-gray-700">
        <div
          data-testid="recommendation-image"
          className="flex-shrink-0 bg-center bg-cover h-40"
          style={ImageStyle}
        />
        <div className="flex flex-col justify-between h-full px-4 py-5">
          <div>
            <Heading
              level="h3"
              text={recommendation.name || t('labels:untitled')}
              align="left"
              margin={0}
            />
            <p className="mb-3 font-regular text-gray-600">
              {recommendation.reference}
            </p>
            {recommendation.description ? (
              <p
                className="mb-3 max-w-[550px] line-clamp-3"
                title={stripHTMLTags(recommendation.description)}
              >
                {stripHTMLTags(recommendation.description)}
              </p>
            ) : (
              <p className="mb-3 text-gray-400">
                {t('components:success:recommendations:noDescription')}
              </p>
            )}
            {recommendation.marketing_claims?.length ? (
              <ul className="mb-5">
                {recommendation.marketing_claims.map(
                  (mc, index) =>
                    getNameforTaxonomyId(mc) && (
                      <li
                        key={index}
                        data-testid="marketing-claim"
                        className="inline-block max-w-full mr-1 uppercase truncate rounded bg-gray-300"
                        style={MarketingClaimStyle}
                        title={getNameforTaxonomyId(mc).toString()}
                      >
                        {getNameforTaxonomyId(mc)}
                      </li>
                    ),
                )}
              </ul>
            ) : null}
          </div>
          <div>
            <a
              href={recommendation.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                onClick(recommendation);
              }}
            >
              <Button label={t('linksOrButtons:details')} />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
